export function normalizeLanguageCode(language?: string): 'pl' | 'en' {
  return String(language || 'pl').toLowerCase().startsWith('en') ? 'en' : 'pl';
}

export function localeForLanguage(language?: string): string {
  return normalizeLanguageCode(language) === 'en' ? 'en-US' : 'pl-PL';
}

/**
 * Thousands are always grouped (staging v3.13, 2026-10-06): Polish CLDR sets
 * minimumGroupingDigits=2, so plain pl-PL formatting printed „6827,05” next to
 * „13 248,00” on one screen, which the business reads as inconsistent. Every
 * shared money AND quantity display helper spreads this option („2 427,03 m²”
 * next to „16 180,20 zł”). Display only — input fields keep their own parser.
 */
export const THOUSANDS_GROUPING = { useGrouping: 'always' } as const;

export function formatLocalizedNumber(
  value: number | null | undefined,
  fractionDigits: number,
  language?: string,
): string {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;

  return safeValue.toLocaleString(localeForLanguage(language), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    ...THOUSANDS_GROUPING,
  } as Intl.NumberFormatOptions);
}

/**
 * An FX rate (e.g. NBP EUR→PLN 4,2537): 2–4 decimals, trailing zeros beyond
 * 2dp trimmed. Not a unit price — prices are 2dp (money.ts formatUnitPrice).
 */
export function formatLocalizedExchangeRate(value: number | null | undefined, language?: string): string {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return safeValue.toLocaleString(localeForLanguage(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
    ...THOUSANDS_GROUPING,
  } as Intl.NumberFormatOptions);
}

export function formatLocalizedPercent(
  value: number | null | undefined,
  language?: string,
  fractionDigits = 1,
): string {
  return `${formatLocalizedNumber(value, fractionDigits, language)}%`;
}

/**
 * Compact physical/commercial quantity: localized, thousands grouped like money,
 * with no artificial trailing zeros („2 427,03”, „809”). Display only.
 */
export function formatLocalizedQuantity(
  value: number | null | undefined,
  language?: string,
  maximumFractionDigits = 3,
): string {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return safeValue.toLocaleString(localeForLanguage(language), {
    maximumFractionDigits,
    ...THOUSANDS_GROUPING,
  } as Intl.NumberFormatOptions);
}
